/**
 * Primary navigation REGISTRY (UX-7; generalizes the X.6.2 nav sections).
 *
 * ONE source of truth — route suffix + capability gate + section + icon +
 * label + surfaces — consumed by:
 *   - the desktop top-bar pill nav (AppShell)
 *   - the mobile bottom tab bar (MobileTabBar)
 *   - the user menu's ROUTE items (AppShell). Non-route chrome — Feedback
 *     (mailto) and Sign out (/.auth/logout) — stays in AppShell by CTO
 *     ruling 2026-07-02: the registry is for navigable routes; putting
 *     auth actions / mailtos in a route registry pollutes the abstraction.
 *   - the Hub's internal-route tiles (hubTiles.ts references entries by
 *     section id and resolves the suffix here — route knowledge lives in
 *     exactly one place; hub PRESENTATION stays in hubTiles.ts).
 *
 * Adding a page = one entry here. The route-coverage test
 * (tests/ui/navRegistryCoverage.test.tsx) walks routes.tsx and FAILS any
 * route that is not (a) a registry entry, (b) a declared detail route
 * (DETAIL_ROUTES), (c) a declared redirect (REDIRECT_ROUTES), or (d) the
 * catch-all — reachability is machine-enforced, not convention.
 *
 * Active-state mapping: an entry is active when the pathname contains one
 * of its activePrefixes (substring semantics preserved from X.6.2 so
 * nested routes light the right pill — /ai-tools/drafts/42 → ai,
 * /organizations/123 → directory). userMenu-only entries carry NO
 * activePrefixes: no chrome surface renders an active state for them, so
 * activeNavSection stays null on /admin-review, /my-suggestions, /merges
 * (pinned by tests).
 */

import {
  Bell,
  ChartColumn,
  Home,
  LayoutGrid,
  MessageSquareText,
  Receipt,
  Search,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  type LucideIcon,
  Clock,
  FileDown,
} from 'lucide-react';
import {
  canOpenAdminConsole,
  canReadClinicalRecord,
  canViewStaffTools,
  canViewInvoice,
  canViewReferralReports,
  canViewReports,
  canViewWorkspace,
  canSuggest,
  canLogInternalTime,
  canExportPayrollHours,
  canUseAiTools,
} from '@/lib/capabilities';
import { canAddLookup } from '@/lib/lookups';
import type { WhoamiResponse } from '@/lib/types';

export type NavSection =
  | 'home'
  // The Team page: Team Roster, Availability, Map and SPICE Schedule as tabs.
  | 'team'
  | 'ai'
  | 'directory'
  | 'clients'
  | 'workspace'
  // The operations Dashboard (nav rework 2026-07-13): the retired X.1
  // CareTree-fed Dashboard was consolidated into the native Operating-metrics
  // page (frozen imports made the old Revenue/Cash&AR cards stale by
  // construction). This section id now owns the /reports/operating route; the
  // old /dashboard route 302-redirects to it (REDIRECT_ROUTES below).
  | 'dashboard'
  | 'reports'
  | 'invoices'
  | 'admin-review'
  | 'my-suggestions'
  // Notifications part two — a person's own rules over the catalogue.
  // userMenu-only, like admin-review/my-suggestions: no chrome surface
  // shows an active state for it (activePrefixes: []).
  | 'notification-settings'
  // My time: a person's hours for the pay period and the place to log
  // internal time. The last desktop pill and a user-menu item; the phone
  // tab bar never carries it, so the user menu is its door there.
  | 'my-time'
  // Payroll hours: everyone's hours for a pay period and the file the
  // office feeds to payroll. userMenu-only, the owner and the payroll seat.
  | 'payroll';

/** Where a registry entry renders. */
export type NavSurface = 'primary' | 'userMenu';

export interface NavEntry {
  section: NavSection;
  label: string;
  /** Path suffix appended after the `/t/{tenantSlug}` prefix. */
  suffix: string;
  /** Single icon source for every consuming surface. */
  icon: LucideIcon;
  /** Optional capability gate; the entry renders only when this returns true. */
  gate?: (whoami: WhoamiResponse | null) => boolean;
  surfaces: ReadonlyArray<NavSurface>;
  /** Pathname substrings that mark this entry active (empty = never active). */
  activePrefixes: readonly string[];
  /** False keeps the entry off the mobile tab bar and its More sheet. Absent means the entry rides the bar like any primary entry. */
  mobileTabBar?: boolean;
}

/** @deprecated UX-7 — kept as an alias for the pre-registry name. */
export type NavLink = NavEntry;

// Primary-nav order (founder-directed through Workspace; GC ruling on the
// tail, PR #529 review): Home, Clients, Directory, Writing, Workspace,
// Dashboard, Invoices, Reports, My time. Dashboard keeps its
// post-Workspace slot, and My time sits last.
// Registry order IS the overflow priority — when the bar can't fit every
// gated item, the tail collapses into the "More" menu first (see PrimaryNav +
// navOverflow.ts). My time is therefore the first pill into More.
export const NAV_REGISTRY: readonly NavEntry[] = [
  { section: 'home', label: 'Home', suffix: '/hub', icon: Home, surfaces: ['primary'], activePrefixes: ['/hub'] },
  // The Clients pill — lands on the task-first CLIENTS LANDING ("Your day"
  // is the Clients landing; the full roster lives behind "All clients →"
  // at /clients/all). Gated read_clinical_record — the same floor the
  // record page itself opens on; the All-clients roster behind it keeps
  // its own narrower gate. activePrefixes '/clients' lights the pill on
  // the landing, the roster, and the record detail pages.
  {
    section: 'clients',
    label: 'Clients',
    suffix: '/clients',
    icon: Users,
    gate: canReadClinicalRecord,
    surfaces: ['primary'],
    // '/care-managers' keeps the pill lit on the CM view.
    activePrefixes: ['/clients', '/care-managers'],
  },
  // The Team page (view_staff_tools, the hub floor every internal seat
  // holds): the directory for everyone, the capacity board and the map as
  // tabs for view_workspace holders. Beside Clients, the other people pill;
  // the hub's People & Office tile is a second door onto the same page.
  {
    section: 'team',
    label: 'Team',
    suffix: '/team',
    icon: Users,
    gate: canViewStaffTools,
    surfaces: ['primary'],
    activePrefixes: ['/team'],
  },
  {
    section: 'directory',
    label: 'Directory',
    suffix: '/directory',
    icon: Search,
    surfaces: ['primary'],
    activePrefixes: ['/directory', '/organizations', '/individuals'],
  },
  // The Writing pill follows the AI tools' own gate: a seat without
  // use_ai_tools gets no pill rather than one that lands on the empty tile.
  {
    section: 'ai',
    label: 'Writing',
    suffix: '/ai-tools',
    icon: Sparkles,
    surfaces: ['primary'],
    activePrefixes: ['/ai-tools'],
    gate: canUseAiTools,
  },
  // PR4a — the Workspace pill, gated to the board's view floor (office +
  // managers; line CMs / mso_staff have no board).
  {
    section: 'workspace',
    label: 'Workspace',
    suffix: '/workspace',
    icon: LayoutGrid,
    gate: canViewWorkspace,
    surfaces: ['primary'],
    activePrefixes: ['/workspace'],
  },
  // Dashboard (nav rework 2026-07-13) — the operations dashboard, keeping the
  // old Dashboard's post-Workspace slot. This is the former "Operating"
  // metrics page (/reports/operating), renamed after the X.1 CareTree-fed
  // Dashboard was retired (frozen imports made its Revenue/Cash&AR cards stale
  // by construction; the operating page supersedes it). The old /dashboard
  // route 302-redirects here (REDIRECT_ROUTES). Gated canViewReports (owner +
  // board_observer), inherited from the Operating page — a DELIBERATE
  // narrowing vs the old view_dashboard_* union (operating data is
  // owner/board tier; widening is a future permissions-screen decision).
  {
    section: 'dashboard',
    label: 'Dashboard',
    suffix: '/reports/operating',
    icon: ChartColumn,
    gate: canViewReports,
    surfaces: ['primary'],
    activePrefixes: ['/reports/operating'],
  },
  // Invoices — view_invoice holders (owner / data_steward / admin_support /
  // exec_support / board_observer).
  {
    section: 'invoices',
    label: 'Invoices',
    suffix: '/invoices',
    icon: Receipt,
    gate: canViewInvoice,
    surfaces: ['primary'],
    activePrefixes: ['/invoices'],
  },
  // Reports Phase A — referral-partner analytics. Gated canViewReferralReports
  // (founder ruling 2026-07-14 — owner, board_observer, data_steward,
  // exec_support) OR canViewReports (so an owner/board_observer who somehow
  // only held the legacy cap still sees the pill); the pill lands on the
  // referral report. activePrefixes narrowed to '/reports/referrals' (not
  // '/reports') so it doesn't also light on the Dashboard '/reports/operating'
  // pill above.
  {
    section: 'reports',
    label: 'Reports',
    suffix: '/reports/referrals',
    icon: TrendingUp,
    gate: (whoami) => canViewReferralReports(whoami) || canViewReports(whoami),
    surfaces: ['primary'],
    activePrefixes: ['/reports/referrals'],
  },
  // User-menu route items (X.5.1 chrome, registry-derived since UX-7).
  // A steward sees Admin only; a non-steward writer sees My suggestions;
  // the profile-editing seats (admin_support / exec_support /
  // supervising_cm) see BOTH, because for them the two are different
  // jobs: Admin is People + Billing, My suggestions is their own queue.
  {
    section: 'admin-review',
    label: 'Admin',
    suffix: '/admin-review',
    icon: ShieldCheck,
    // Must equal the AdminReview page gate. canOpenAdminConsole is the
    // single shared predicate (2026-08-31: the previous inline gate
    // omitted manage_staff_profiles / manage_permissions, so authorized
    // seats had no link to a page that admitted them).
    gate: canOpenAdminConsole,
    surfaces: ['userMenu'],
    activePrefixes: [],
  },
  {
    section: 'my-suggestions',
    label: 'My suggestions',
    suffix: '/my-suggestions',
    icon: MessageSquareText,
    gate: (whoami) => canSuggest(whoami) && !canAddLookup(whoami),
    surfaces: ['userMenu'],
    activePrefixes: [],
  },
  // Notifications part two — every signed-in member's own rules over
  // the catalogue. No CAPABILITY gate (it is a personal-account page,
  // same floor as the bell itself — view_staff_tools gates the BELL's
  // rendering in AppShell, not this route), but it still needs a
  // resolved session: gate on whoami being non-null so it doesn't
  // appear before auth resolves.
  // My time: every roster login that may log internal time gets the page
  // (log_internal_time); the external and board seats do not. A desktop
  // pill and a user-menu item, never the phone tab bar (mobileTabBar:
  // false): it is the only entry with two surfaces, because it is the
  // only one kept off the phone bar and needs a second door there.
  {
    section: 'my-time',
    label: 'My time',
    suffix: '/my-time',
    icon: Clock,
    gate: canLogInternalTime,
    surfaces: ['primary', 'userMenu'],
    activePrefixes: ['/my-time'],
    mobileTabBar: false,
  },
  // Payroll hours: the pay-period file (export_payroll_hours: the owner
  // and the payroll seat by default).
  {
    section: 'payroll',
    label: 'Payroll hours',
    suffix: '/payroll',
    icon: FileDown,
    gate: canExportPayrollHours,
    surfaces: ['userMenu'],
    activePrefixes: [],
  },
  {
    section: 'notification-settings',
    label: 'Notification settings',
    suffix: '/settings/notifications',
    icon: Bell,
    gate: (whoami) => whoami !== null,
    surfaces: ['userMenu'],
    activePrefixes: [],
  },
];

/** @deprecated UX-7 — kept as an alias for the pre-registry name. */
export const NAV_LINKS = NAV_REGISTRY;

/** The primary-nav links the given user may see (the desktop pill row). */
export function visibleNavLinks(whoami: WhoamiResponse | null): readonly NavEntry[] {
  return NAV_REGISTRY.filter(
    (l) => l.surfaces.includes('primary') && (!l.gate || l.gate(whoami)),
  );
}

/** The primary-nav links the given user may see on the phone tab bar: visibleNavLinks minus any entry marked mobileTabBar: false. */
export function mobileTabBarLinks(whoami: WhoamiResponse | null): readonly NavEntry[] {
  return visibleNavLinks(whoami).filter((l) => l.mobileTabBar !== false);
}

/** The user-menu route items the given user may see. */
export function userMenuLinks(whoami: WhoamiResponse | null): readonly NavEntry[] {
  return NAV_REGISTRY.filter(
    (l) => l.surfaces.includes('userMenu') && (!l.gate || l.gate(whoami)),
  );
}

/**
 * Derive the active primary section from a full pathname (tenant prefix and
 * all). First registry entry with a matching activePrefix wins; prefixes are
 * mutually disjoint substrings so order is not load-bearing. Returns null
 * when the route is outside the primary sections (e.g. admin-review).
 */
export function activeNavSection(pathname: string): NavSection | null {
  for (const entry of NAV_REGISTRY) {
    if (entry.activePrefixes.some((p) => pathname.includes(p))) return entry.section;
  }
  return null;
}

// ---------------------------------------------------------------------
// Route-coverage accounting — consumed ONLY by the coverage test, which
// asserts every route in routes.tsx is claimed by exactly one bucket:
// a registry suffix, a DETAIL_ROUTES entry, a REDIRECT_ROUTES entry, or
// the '*' catch-all. Declaring a detail page or redirect here is the
// documented act the invariant requires.
// ---------------------------------------------------------------------

/** Detail/child routes → the registry section that owns them. */
export const DETAIL_ROUTES: Readonly<Record<string, NavSection>> = {
  // The Bake Off League: reached from its Home link while
  // features.bake_off_league is on; never a nav item.
  '/t/:tenantSlug/bake-off': 'home',
  '/t/:tenantSlug/invoices/:id': 'invoices',
  '/t/:tenantSlug/invoices/prebilling': 'invoices',
  '/t/:tenantSlug/invoices/clients/:clientId': 'invoices',
  '/t/:tenantSlug/organizations/:id': 'directory',
  '/t/:tenantSlug/individuals/:id': 'directory',
  '/t/:tenantSlug/ai-tools/email': 'ai',
  '/t/:tenantSlug/ai-tools/assessment': 'ai',
  '/t/:tenantSlug/ai-tools/assessment-letter': 'ai',
  '/t/:tenantSlug/ai-tools/brand-formatter': 'ai',
  '/t/:tenantSlug/ai-tools/med-list': 'ai',
  '/t/:tenantSlug/ai-tools/intake': 'ai',
  '/t/:tenantSlug/ai-tools/drafts': 'ai',
  '/t/:tenantSlug/ai-tools/drafts/:id': 'ai',
  // Reached from admin-review surfaces; gated canMergeProviders at the page.
  '/t/:tenantSlug/merges/:eventId': 'admin-review',
  // TS-1 — the full client roster (Include-archived + Add-client), behind
  // the landing's "All clients →" link since R9 made the task-first page
  // the /clients landing. Gated view_all_clinical at the page for the soak.
  '/t/:tenantSlug/clients/all': 'clients',
  // Kith client record page — the record surface, in the 'clients' nav
  // section (the Clients pill lights on it via the '/clients'
  // activePrefix). Page gate is read_clinical_record; the legacy
  // /clients/:clientId/notes still redirects here (below).
  '/t/:tenantSlug/clients/:clientId': 'clients',
  // Soak #2 top-bar #11 — the care-manager view (a CM + their clients),
  // reached from the record header's "Care manager" link. Part of the
  // clients area; the '/care-managers' activePrefix (below) lights the pill.
  '/t/:tenantSlug/care-managers/:rosterId': 'clients',
  // The bell's "See all" — reached from the bell panel footer, not from
  // the registry surfaces; sits with its own settings page for the
  // coverage accounting.
  '/t/:tenantSlug/notifications': 'notification-settings',
};

/** Documented redirects (root bootstrap + the three 302-indefinite legacy paths). */
export const REDIRECT_ROUTES: readonly string[] = [
  '/',
  '/t/:tenantSlug/lookups',
  '/t/:tenantSlug/suggestions',
  '/t/:tenantSlug/bulk-add',
  // PR-C1: the legacy standalone notes surface now redirects to the client
  // record page's Notes tab (/clients/:clientId?tab=notes).
  '/t/:tenantSlug/clients/:clientId/notes',
  // R9: the retired "Your day" address — the task-first page IS the
  // Clients landing now; old bookmarks hop to /clients.
  '/t/:tenantSlug/home',
  // Nav rework 2026-07-13: the retired X.1 Dashboard route. The native
  // operations Dashboard lives at /reports/operating now; old /dashboard
  // bookmarks 302-redirect there (routes.tsx DashboardRedirect).
  '/t/:tenantSlug/dashboard',
  // The capacity board's old Workspace address: it is the Team page's
  // capacity tab now (routes.tsx CapacityRedirect).
  '/t/:tenantSlug/workspace/capacity',
];
